// Shared date logic for the client follow-up TASK cadence (PKT-0245).
//
// client_tasks.due_date is a Postgres DATE column, so supabase-js returns a
// bare "YYYY-MM-DD" string. The one rule that matters here: NEVER round-trip a
// date-only string through `new Date("YYYY-MM-DD")` — JS parses that as UTC
// midnight, which is the *previous* calendar day for any negative-UTC-offset
// operator (the entire ATL/Eastern user base). That shifted every day-0
// cadence task to "overdue" the moment it was created. So all comparisons here
// are done on LOCAL calendar-date STRINGS, and due dates are built from local
// calendar parts. Centralized so the per-client timeline and the dashboard
// worklist can never disagree about what "today" means.

export type DueBucket = "overdue" | "today" | "upcoming" | "none";

// Local calendar date as "YYYY-MM-DD" (defaults to now).
export function localDateStr(date: Date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

// due_date for `assignment date + offsetDays`, as a local "YYYY-MM-DD".
export function dueDateForOffset(offsetDays: number, from: Date = new Date()): string {
  const d = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  d.setDate(d.getDate() + offsetDays);
  return localDateStr(d);
}

// Normalize a stored due_date (date or timestamp string) to its date part.
function dueDatePart(dueDate: string): string {
  return dueDate.slice(0, 10);
}

// Classify a due_date relative to local today. `todayStr` is injectable for tests.
export function dueBucket(
  dueDate?: string | null,
  todayStr: string = localDateStr()
): DueBucket {
  if (!dueDate) return "none";
  const due = dueDatePart(dueDate);
  if (due < todayStr) return "overdue";
  if (due === todayStr) return "today";
  return "upcoming";
}

// True when the task is due before local today.
export function isOverdue(
  dueDate?: string | null,
  todayStr: string = localDateStr()
): boolean {
  if (!dueDate) return false;
  return dueDatePart(dueDate) < todayStr;
}

/** Render a DATE as its calendar day; timestamps retain the viewer's timezone. */
export function formatTaskDueDate(value: string): string {
  const date = new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T12:00:00` : value);
  return Number.isNaN(date.getTime()) ? 'Unknown date' : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
